import Description from '../description'

/**
 * A (value, description) pair.
 * The description "explains" the value, hence interface name Explanation.
 *
 * @template T The type of the stored value.
 *
 * @property value - The stored value.
 * @property description - A description or explanation of the stored value.
 */
export interface Explanation<T> {
  readonly value: T
  readonly description: Description
}

/**
 * A shorthand function for creating an explanation object.
 *
 * @template T The type of the value to be explained.
 *
 * @param value - The value for the explanation.
 * @param description - The description for the explanation.
 * @returns The explanation object, a (value, description) pair.
 */
export function explain<T>(value: T, description: string): Explanation<T> {
  return {
    value,
    description: new Description(description),
  }
}

/**
 * A shorthand function for creating an explanation object.
 * Deals with potentially undefined values.
 *
 * @template T The type of the value to be explained.
 *
 * @param value - The value for the explanation (or undefined).
 * @param descriptionCreator - A function that creates a description given the value.
 * @returns The explanation object, a (value, description) pair (or undefined).
 */
export function explainOptional<T>(
  value: T | undefined,
  descriptionCreator: (_: T) => string,
): Explanation<T> | undefined {
  return value === undefined
    ? undefined
    : explain(value, descriptionCreator(value))
}

/**
 * A shorthand function for creating an explanation object.
 * Explains arrays.
 *
 * @template TArray The type of the array elements.
 * @template TNewArray The type of the explanations for each array element.
 * @param value - The original array of values to be explained.
 * @param explainElement - A function that explains individual elements of the array.
 * @param description - The description for the entire array.
 * @returns The explanation object, a (value, description) pair. The value is the original array mapped using explainElement.
 */
export function explainArray<TArray, TNewArray>(
  value: TArray[],
  explainElement: (_: TArray) => TNewArray,
  description: string,
): Explanation<TNewArray[]> {
  return explain(value.map(explainElement), description)
}

/**
 * A shorthand function for creating an explanation object.
 * Explains arrays. Deals with potentially undefined values.
 *
 * @template TArray The type of the array elements.
 * @template TNewArray The type of the explanations for each array element.
 * @param value - The original array of values to be explained (or undefined).
 * @param explainElement - A function that explains individual elements of the array.
 * @param description - The description for the entire array.
 * @returns The explanation object, a (value, description) pair (or undefined).
 * The value is the original array mapped using explainElement.
 */
export function explainOptionalArray<TArray, TNewArray>(
  value: TArray[] | undefined,
  explainElement: (_: TArray) => TNewArray,
  description: string,
): Explanation<TNewArray[]> | undefined {
  return value === undefined
    ? undefined
    : explainArray(value, explainElement, description)
}
